import { Permissions, RequestPermissions } from '@/app/types';
import CancelledNotice from '@/app/pages/LobbyPage/CancelledNotice';
import DevicesRefusedDialog from '@/app/pages/LobbyPage/DevicesRefusedDialog';
import type { Refusal } from '@/app/pages/LobbyPage';
import LobbyAppointment from '@/app/pages/LobbyPage/LobbyAppointment';
import LobbyHeader from '@/app/pages/LobbyPage/LobbyHeader';
import LobbyJoinButton from '@/app/pages/LobbyPage/LobbyJoinButton';
import LobbyJoinFlow from '@/app/pages/LobbyPage/LobbyJoinFlow';
import LobbyVideo from '@/app/pages/LobbyPage/LobbyVideo';
import ConsentNotice from '@/app/pages/LobbyPage/ConsentNotice';
import HostRecordingNotice from '@/app/pages/LobbyPage/HostRecordingNotice';
import OpenInAnotherTabNotice from '@/app/pages/LobbyPage/OpenInAnotherTabNotice';
import type { SettledMeetingCopy } from '@/app/queries/MeetingContextQuery/useMeetingCopy';
import useLocationPermission from '@/app/hooks/useLocationPermission';
import useCompassPermission from '@/app/hooks/useCompassPermission';
import { cn } from '@/lib/utils';

interface ILobbyPageProps {
  startTime: string;
  address: string;
  isCancelled: boolean;
  currentMeetingUrl: string | null;
  customerName?: string;
  staffName?: string;
  permissions: Permissions;
  denied: Permissions;
  refusal: Refusal | null;
  requestPermissions: RequestPermissions;
  videoStream?: MediaStream;
  onJoin(): void;
  onJoinWithout(): void;
  onStay(): void;
  onTryAgain(): void;
  isCallOpenElsewhere: boolean;
  isClient: boolean;
  copy: Pick<SettledMeetingCopy, 'title' | 'hostTitle' | 'isSettled'>;
}

export default function LobbyPage({
  startTime, denied, refusal, address, customerName, staffName, permissions, requestPermissions,
  videoStream, onJoin, onJoinWithout, onStay, onTryAgain, isCallOpenElsewhere, isClient, copy, isCancelled,
  currentMeetingUrl,
}: ILobbyPageProps) {
  const openElsewhereNotice = isCallOpenElsewhere && <OpenInAnotherTabNotice />;

  // Owned here rather than in the chip, because the readiness heading above the
  // button can only claim "ready" if it knows about all three permissions. Only
  // the customer walks the property, so only they are asked for location.
  const location = useLocationPermission(isClient);
  const compass = useCompassPermission(isClient);

  const joinButton = (
    <LobbyJoinButton
      currentMeetingUrl={currentMeetingUrl} isCancelled={isCancelled} isClient={isClient}
      onJoin={onJoin} />
  );

  return (
    <div
      className={cn(
        'box-border relative flex flex-col items-center bg-white dark:bg-gray-900 px-5 md:px-10 lg:px-12 pt-6',
        'w-full h-max min-h-screen font-base text-gray-900 dark:text-gray-100 leading-normal',
      )}>
      {/* One centred column holds the page. Margin auto rather than
          justify-center, so a lobby taller than the window scrolls from the top
          instead of losing its head. */}
      <div className="md:my-auto w-full max-w-6xl">
        <section className="gap-10 lg:gap-14 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-9 xl:grid-cols-2 pb-8 w-full">
          {/* The preview stays put while the notice and the checklist scroll
              past it: on a short window it would otherwise be the first thing
              to leave the screen, and it is what the customer came to check. */}
          <div
            className={cn(
              'hidden md:top-6 md:sticky md:flex md:flex-col md:self-start',
              'md:col-span-2 lg:col-span-5 xl:col-span-1',
            )}>
            <LobbyVideo notice={openElsewhereNotice} videoStream={videoStream} />
          </div>

          <div className="flex flex-col gap-7 md:col-span-1 lg:col-span-4 xl:col-span-1 w-full">
            <LobbyHeader
              copy={copy} customerName={customerName} isClient={isClient} staffName={staffName}
              startTime={startTime} />

            {isCancelled && (
              <CancelledNotice currentMeetingUrl={currentMeetingUrl} hostTitle={copy.hostTitle} isClient={isClient} />
            )}

            <div className="md:hidden">
              <LobbyVideo notice={openElsewhereNotice} videoStream={videoStream} />
            </div>

            {isClient ? <ConsentNotice /> : <HostRecordingNotice />}

            <LobbyJoinFlow
              compass={compass}
              denied={denied}
              isClient={isClient}
              joinButton={joinButton}
              location={location}
              permissions={permissions}
              requestPermissions={requestPermissions} />

            {/* Reference material once the decision is made, so it carries no
                card of its own — a rule and a label are enough. */}
            <LobbyAppointment
              address={address} copy={copy} customerName={customerName} isClient={isClient}
              staffName={staffName} />
          </div>
        </section>
      </div>

      {/* On a phone the notice and the checklist push the only button that
          matters past the fold, so it rides along the bottom instead. */}
      <div
        className={cn(
          'md:hidden bottom-0 z-10 sticky self-stretch bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm',
          '-mx-5 mt-auto p-5 border-gray-200 dark:border-gray-800 border-t',
        )}>
        {joinButton}
      </div>

      {refusal && (
        <DevicesRefusedDialog
          {...refusal}
          hostTitle={copy.hostTitle}
          isClient={isClient}
          onJoinWithout={onJoinWithout}
          onStay={onStay}
          onTryAgain={onTryAgain} />
      )}
    </div>
  );
}
